import { Button, Descriptions, Skeleton, Space, Typography } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import CopyToClipboard from '../components/copy-to-clipboard';
import DetailPageHeader from '../components/detail-page-header';
import { EMPTY_VALUE } from '../fragments/empty-value';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import { downloaderLoadWarehouseProperty } from '../reducers/downloader/reducer';
import { SPACING } from '../theme/tokens';

function DownloaderWarehouseProperty(): React.ReactElement {
  const dispatch = useAppDispatch();
  const downloaderProperty = useSelector(
    (s: RootState) => s.Downloader.property,
  );

  const { rawName } = useParams();
  const [showPropertyValues, setShowPropertyValues] = useState<boolean>(false);

  const reload = useCallback((): void => {
    if (rawName) {
      dispatch(downloaderLoadWarehouseProperty(rawName));
    }
  }, [dispatch, rawName]);

  useEffect((): void => {
    reload();
  }, [reload]);

  if (!downloaderProperty) {
    return <Skeleton active />;
  }

  return (
    <>
      <DetailPageHeader title="Warehouse property" />

      <Descriptions
        bordered
        column={{ xxl: 1, xl: 1, lg: 1, md: 1, sm: 1, xs: 1 }}
        items={[
          {
            key: 'raw-name',
            label: 'Raw name',
            children: (
              <Space size={SPACING.lg}>
                {downloaderProperty.rawName}

                <CopyToClipboard
                  text={downloaderProperty.rawName}
                  type="link"
                />
              </Space>
            ),
          },
          {
            key: 'name',
            label: 'Name',
            children: (
              <Space size={SPACING.lg}>
                {downloaderProperty.name}

                <CopyToClipboard text={downloaderProperty.name} type="link" />
              </Space>
            ),
          },
          {
            key: 'unit',
            label: 'Unit',
            children: (
              <Space size={SPACING.lg}>
                {downloaderProperty.unit || EMPTY_VALUE}

                {downloaderProperty.unit && (
                  <CopyToClipboard text={downloaderProperty.unit} type="link" />
                )}
              </Space>
            ),
          },
          {
            key: 'values',
            label: 'Values',
            children: (
              <Space size={SPACING.lg} align="start" orientation="vertical">
                <Space size={SPACING.lg}>
                  <Typography.Text>
                    {downloaderProperty.values?.length || 0} items
                  </Typography.Text>

                  <Button
                    color="primary"
                    onClick={() => setShowPropertyValues(!showPropertyValues)}
                  >
                    {showPropertyValues ? 'Hide' : 'Show'}
                  </Button>
                </Space>

                {showPropertyValues && (
                  <div>
                    {(downloaderProperty.values ?? []).map((item: string) => (
                      <div key={item}>
                        <Space size={SPACING.lg}>
                          {item}
                          <CopyToClipboard text={item} type="link" />
                        </Space>
                      </div>
                    ))}
                  </div>
                )}
              </Space>
            ),
          },
        ]}
      />
    </>
  );
}

export default DownloaderWarehouseProperty;
